import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  LogOut,
  Warehouse,
  XCircle,
} from "lucide-react";
import probrokerLogo from "../assets/probroker-logo.png";
import PropertyCard from "../components/PropertyCard";
import OwnerEditPropertyModal from "../components/Ownerdashboard/OwnerEditPropertyModal";
import InstallAppButton from "../components/Ownerdashboard/InstallAppButton";
import {
  fetchOwnerProperties,
  getOwnerNumber,
  ownerLogout,
} from "../components/Ownerdashboard/ownerAuth";

const PAGE_LIMIT = 20;

const Ownerdashboard = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [pagination, setPagination] = useState({});
  const [serverStats, setServerStats] = useState(null);
  const [number, setNumber] = useState(getOwnerNumber());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [editingProperty, setEditingProperty] = useState(null);

  const loadProperties = useCallback(
    async (pageToLoad) => {
      setLoading(true);
      setError("");

      const result = await fetchOwnerProperties({ page: pageToLoad, limit: PAGE_LIMIT });

      if (result.success) {
        setProperties(result.properties);
        setPagination(result.pagination);
        if (result.stats) setServerStats(result.stats);
        if (result.number) setNumber(result.number);
      } else {
        setProperties([]);
        setError(result.message);

        // Session expired or revoked — the token is already cleared.
        if (result.unauthorized) {
          navigate("/owner/login");
          return;
        }
      }

      setLoading(false);
    },
    [navigate]
  );

  useEffect(() => {
    loadProperties(page);
  }, [page, loadProperties]);

  const handleLogout = async () => {
    await ownerLogout();
    navigate("/owner/login");
  };

  const total = pagination?.total ?? properties.length;
  const totalPages = pagination?.totalPages ?? 1;

  // Counters come from the dashboard endpoint's `stats` block; the local
  // fallbacks only cover older server versions that don't send it yet.
  const pendingFallback = properties.filter(
    (property) =>
      property?.ownerUpdatePending === true ||
      property?.pendingUpdate === true ||
      property?.updateStatus === "pending" ||
      property?.approvalStatus === "pending"
  ).length;

  const totalCount = serverStats?.totalProperties ?? total;
  const pendingCount = serverStats?.pendingUpdate ?? pendingFallback;
  const liveCount = serverStats?.liveUpdate ?? Math.max(total - pendingFallback, 0);
  const rejectedCount = serverStats?.rejectedUpdate ?? 0;

  const stats = [
    {
      label: "Total Properties",
      value: totalCount,
      hint: "All your listings on PRObroker",
      Icon: Warehouse,
      accent: "#7c3aed",
      iconClass: "bg-[#7c3aed]/10 text-[#7c3aed]",
    },
    {
      label: "Pending Update",
      value: pendingCount,
      hint: "Waiting for your changes to go live",
      Icon: Clock,
      accent: "#f59e0b",
      iconClass: "bg-amber-100 text-amber-500",
    },
    {
      label: "Live & Updated",
      value: liveCount,
      hint: "Successfully published & verified",
      Icon: CheckCircle2,
      accent: "#10b981",
      iconClass: "bg-emerald-100 text-emerald-500",
    },
    // Only takes a slot when something was actually rejected.
    ...(rejectedCount > 0
      ? [
          {
            label: "Rejected Update",
            value: rejectedCount,
            hint: "Changes that need a correction",
            Icon: XCircle,
            accent: "#ef4444",
            iconClass: "bg-red-100 text-red-500",
          },
        ]
      : []),
  ];

  return (
    <div className="min-h-screen w-full bg-[#f6f6fe]">
      <header className="sticky top-0 z-20 border-b border-gray-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 md:px-6">
          <img src={probrokerLogo} alt="PRObroker" className="h-7 w-auto sm:h-8" />
          <div className="flex flex-wrap items-center gap-3">
            {/* The badge has to live outside the <a>, which clips its children
                (overflow-hidden keeps the rotating gradient inside the pill). */}
            <div className="relative inline-flex">
              {/* Rotating conic-gradient sits behind a 2px padding ring, which reads
                  as a light travelling around the border. */}
              <a
                href="https://ownerlisting.probroker.in/"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-flex overflow-hidden rounded-full p-[2px] shadow-sm transition-transform duration-200 hover:scale-[1.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#503691] focus-visible:ring-offset-2"
                title="Open Owner Property Listing in a new tab"
              >
                <span className="absolute inset-[-1000%] animate-spin-slow bg-[conic-gradient(from_90deg_at_50%_50%,#503691_0%,#FFFFFF_50%,#503691_100%)]" />
                <span className="relative inline-flex h-full w-full items-center gap-2 whitespace-nowrap rounded-full bg-[#503691] px-4 py-2 text-xs font-semibold text-white transition-colors group-hover:bg-[#3f2a73] sm:px-5 sm:text-sm">
                  <Building2 size={15} />
                  <span className="hidden sm:inline">Property Listing</span>
                  <span className="sm:hidden">Property Listing</span>
                  <ExternalLink
                    size={13}
                    className="opacity-60 transition-opacity group-hover:opacity-100"
                  />
                </span>
              </a>

              {/* FREE tag on the top-right corner: a ping halo behind a pulsing pill */}
              <span className="pointer-events-none absolute -right-2 -top-2 z-10 inline-flex">
                <span className="relative inline-flex animate-free-pop items-center rounded-full border border-white bg-emerald-500 px-2 py-0.5 text-[10px] font-extrabold uppercase leading-tight tracking-wide text-white shadow-md">
                  Free
                </span>
              </span>
            </div>

           
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 rounded-full border border-[#503691] px-4 py-2 text-sm font-semibold text-[#503691] transition-colors hover:bg-[#503691] hover:text-white"
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Not a <main> element: checkout.css declares a global `main { display: flex }`
          that would lay this out as a centered row. */}
      <div className="w-full px-2 py-4 md:px-4">
        {/* Everything on the page shares this 90% column, so the header, stat
            row and property cards line up on the same left/right edges */}
        <div className="mx-auto w-[90%]">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h1 className="text-xl font-bold text-gray-800">My Properties</h1>
            {!loading && !error && (
              <p className="text-gray-500">
                <span className="font-semibold text-[#7c3aed]">{total}</span>{" "}
                {total === 1 ? "property" : "properties"} found
              </p>
            )}
          </div>

          {!loading && !error && (
            <div
              className={`grid grid-cols-1 gap-4 py-4 ${
                stats.length === 4 ? "sm:grid-cols-2 xl:grid-cols-4" : "sm:grid-cols-3"
              }`}
            >
              {stats.map(({ label, value, hint, Icon, accent, iconClass }) => (
                <div
                  key={label}
                  className="relative overflow-hidden rounded-2xl bg-white p-5 shadow-sm"
                >
                  <span
                    className="absolute inset-y-0 left-0 w-1.5"
                    style={{ backgroundColor: accent }}
                  />
                  <div className="flex items-center gap-4 pl-2">
                    <span
                      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${iconClass}`}
                    >
                      <Icon size={22} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-700">{label}</p>
                      <p className="text-3xl font-bold leading-tight text-gray-900">{value}</p>
                      <p className="mt-1 truncate text-xs text-gray-400">{hint}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {loading && (
            <div className="grid grid-cols-1">
              {[1, 2, 3].map((key) => (
                <div key={key} className="gap-4 mt-2 md:my-4">
                  <div className="animate-pulse rounded-lg bg-white p-2 shadow-md lg:p-4">
                    <div className="flex flex-wrap items-center gap-2 pt-4 px-2 xl:px-4">
                      <div className="h-7 w-28 rounded-full bg-gray-200" />
                      <div className="h-7 w-28 rounded-full bg-gray-200" />
                      <div className="h-9 w-56 rounded-full bg-gray-200" />
                    </div>
                    <div className="grid grid-cols-1 gap-4 p-2 lg:grid-cols-2 lg:p-4">
                      <div className="h-40 rounded bg-gray-100" />
                      <div className="h-40 rounded bg-gray-100" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
              <p className="font-medium text-red-600">{error}</p>
              <button
                type="button"
                onClick={() => loadProperties(page)}
                className="mt-4 rounded-full bg-[#503691] px-6 py-2 text-sm font-semibold text-white hover:bg-[#5b54e6]"
              >
                Try again
              </button>
            </div>
          )}

          {!loading && !error && properties.length === 0 && (
            <div className="rounded-lg border border-gray-200 bg-white p-10 text-center">
              <Building2 size={40} className="mx-auto mb-3 text-gray-300" />
              <p className="font-semibold text-gray-800">No properties found</p>
              <p className="mt-1 text-sm text-gray-500">
                No listings are registered against +91 {number || "this number"}.
              </p>
            </div>
          )}

          {!loading && !error && properties.length > 0 && (
            <>
              <div className="grid grid-cols-1">
                {properties.map((property) => (
                  <div key={property._id || property.propertyId} className="gap-4 mt-2 md:my-4">
                    <PropertyCard
                      property={property}
                      ownerView
                      onEdit={setEditingProperty}
                      onMediaUpdated={() => loadProperties(page)}
                      onStatusUpdated={(result) => {
                        if (result?.unauthorized) {
                          navigate("/owner/login");
                          return;
                        }
                        loadProperties(page);
                      }}
                    />
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-center gap-4">
                  <button
                    type="button"
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={!pagination?.hasPrevPage}
                    className="inline-flex items-center gap-1 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronLeft size={16} /> Prev
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {pagination?.page || page} of {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((prev) => prev + 1)}
                    disabled={!pagination?.hasNextPage}
                    className="inline-flex items-center gap-1 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Next <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {editingProperty && (
        <OwnerEditPropertyModal
          property={editingProperty}
          onClose={() => setEditingProperty(null)}
          onUpdated={(result) => {
            setEditingProperty(null);
            if (result?.unauthorized) {
              navigate("/owner/login");
              return;
            }
            loadProperties(page);
          }}
        />
      )}

      <InstallAppButton />
    </div>
  );
};

export default Ownerdashboard;
